import { SourceStore, SynonymAssociationStore } from '@seedlang/stores';
import { isPresent } from '@seedlang/utils';
import autobind from 'autobind-decorator';
import Alert from 'components/alert';
import CheckIfTrue from 'components/check_if_true';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import InfoTooltip from 'components/info_tooltip';
import Modal from 'components/modal';
import Spinner from 'components/spinner';
import Text from 'components/text';
import { flatten, uniqBy } from 'lodash';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router';
import { Theme } from '@seedlang/constants';

@observer
class SynonymsEditModal extends React.Component {
  @observable movedSynonymMessage = null;

  constructor(props) {
    super(props);
    if (!SourceStore.hasIndexData) {
      const sourceIds = props.word.acceptedEnglishTranslationSources.map(item => item.source?.id);
      if (isPresent(sourceIds)) {
        SourceStore.getIndex({filters: {id: sourceIds}});
      }
    }
  }

  @computed get potentialSynonyms() {
    const wordSourceIds = SourceStore.indexData?.map(source => source.id) || [];

    const wordsWithCommonSources = uniqBy(
      flatten(
        SourceStore.indexData?.map(source =>
          source.translations.filter(
            item =>
              item.languageId === this.props.word.languageId &&
              item.id !== this.props.word.id &&
              item.root &&
              item.wordTypeName === this.props.word.wordTypeName,
          ),
        ),
      ),
      item => item.id,
    );

    const existingSynonyms = this.props.word.synonymAssociations
      ?.filter(sa => sa.synonym && sa.synonym.id !== this.props.word.id)
      .map(sa => sa.synonym) || [];

    const allPotentialWords = uniqBy([...wordsWithCommonSources, ...existingSynonyms], 'id');

    return allPotentialWords.map(word => {
      const wordSources = word.translationSources?.filter(ts => ts.source?.languageId === 'EN').map(ts => ts.source?.id) || [];
      const commonSourcesCount = wordSources.filter(sourceId => wordSourceIds.includes(sourceId)).length;

      return {
        ...word,
        commonSourcesCount,
      };
    }).sort((a, b) => b.commonSourcesCount - a.commonSourcesCount);
  }

  @autobind synonymAssociation(wordId) {
    return this.props.word.synonymAssociations.find(item => item.synonym?.id === wordId);
  }

  @autobind onToggleSynonym(wordId) {
    if (isPresent(this.synonymAssociation(wordId))) {
      SynonymAssociationStore.destroy({ids: {synonymAssociationId: this.synonymAssociation(wordId).id}}, this.props.afterChange);
    } else {
      SynonymAssociationStore.create({data: {wordId: this.props.word.id, synonymId: wordId}}, this.props.afterChange);
    }
  }

  @autobind onToggleMeaning(wordId, meaning) {
    const existingAssociation = this.synonymAssociation(wordId);

    if (isPresent(existingAssociation)) {
      if (existingAssociation.meaningId === meaning.id) {
        SynonymAssociationStore.update({ids: {synonymAssociationId: existingAssociation.id}, data: {meaningId: null}}, this.props.afterChange);
      } else {
        const previousMeaningId = existingAssociation.meaningId;

        if (previousMeaningId && previousMeaningId !== meaning.id) {
          const previousMeaning = this.props.word.meanings?.find(m => m.id === previousMeaningId);
          if (previousMeaning) {
            const synonym = existingAssociation.synonym;
            this.movedSynonymMessage = `Synonym "${synonym?.targetText}" was moved from meaning #${previousMeaning.position} to meaning #${meaning.position}`;
            setTimeout(() => { this.movedSynonymMessage = null; }, 5000);
          }
        }

        SynonymAssociationStore.update({ids: {synonymAssociationId: existingAssociation.id}, data: {meaningId: meaning.id}}, this.props.afterChange);
      }
    } else {
      SynonymAssociationStore.create({data: {wordId: this.props.word.id, synonymId: wordId, meaningId: meaning.id}}, this.props.afterChange);
    }
  }

  render() {
    return (
      <Modal
        onCloseModal={this.props.onClose}
        gradient
        title={`Manage synonyms for "${this.props.word.targetText}"`}
        contentShouldScroll
        maxHeight={500}
      >
        <Text margin='0 0 10px'>Synonyms can only be selected among other words that have at least one source in common among <i>{this.props.word.sourceList}</i>.</Text>
        {
          isPresent(this.props.meaning) &&
            <Alert>You are looking at synonyms for the meaning n°{this.props.meaning.position} (<i>{this.props.meaning.enSourcesString}</i>).</Alert>
        }
        {
          isPresent(this.movedSynonymMessage) &&
            <Alert background={Theme.green}>{this.movedSynonymMessage}</Alert>
        }
        {
          !SourceStore.hasIndexData &&
            <Spinner />
        }
        {
          SourceStore.hasIndexData && isPresent(this.potentialSynonyms) &&
          <table className='table-wrapper'>
            <thead>
              <tr>
                <th width='40px'/>
                <th>Word</th>
                <th>Common Sources</th>
                <th>
                  Has Meaning
                  <InfoTooltip>
                    <div>
                      {
                        this.props.meaning ?
                          'Shows if this synonym is linked to the current meaning' :
                          'Shows if this word has any meanings defined'
                      }
                    </div>
                    </InfoTooltip>
                </th>
                <th>
                  <span>
                    Is Synonym
                  </span>
                  <InfoTooltip>
                    <div>
                      {
                        this.props.meaning ?
                        `Indicates whether this word is a synonym of "${this.props.word.targetText}"` :
                        `Check to mark this word as a synonym of "${this.props.word.targetText}"`
                      }
                    </div>
                  </InfoTooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              {
                this.potentialSynonyms.map(item => {
                  return (
                    <tr key={item.id}>
                      <td>
                        <Link
                          to={{ name: 'builder.words.edit', params: { wordId: item.id } }}
                          className='on-click'
                          target='_blank'
                        >
                          <i className='fa fa-chevron-right fa-block' />
                        </Link>
                      </td>
                      <td>{item.targetText}</td>
                      <td>{item.commonSourcesCount}</td>
                      <td>
                        {
                          isPresent(this.props.meaning) ?
                            <InPlaceCheckbox
                              value={isPresent(this.synonymAssociation(item.id)) && this.synonymAssociation(item.id)?.meaningId == this.props.meaning.id}
                              onChange={() => this.onToggleMeaning(item.id, this.props.meaning)}
                            />
                          :
                            <CheckIfTrue
                              circle
                              value={isPresent(item.meaningId)}
                              width='20px'
                            />
                        }
                      </td>
                      <td>
                        <InPlaceCheckbox
                          value={isPresent(this.synonymAssociation(item.id))}
                          onChange={() => this.onToggleSynonym(item.id)}
                        />
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        }
      </Modal>
    );
  }
}

export default SynonymsEditModal;